import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { formatCurrency } from "../../utils/billing-utils";
import type { Referral } from "../types/commissions.types";

interface ReferralsTableProps {
  referrals: Referral[];
  quarterLabel: string;
  onRowClick: (referral: Referral) => void;
  /** Optional map of company id -> display name for resolving customer names */
  companyIdToName?: Record<string, string>;
  /** Optional map of referral id -> summed estimated commission for the period (from reports) */
  referralIdToEstimatedCommission?: Record<string, number>;
}

function getCustomersCellContent(
  ref: Referral,
  companyIdToName?: Record<string, string>
): string {
  const lines = ref.referralLines ?? ref.lines ?? [];
  const uniqueIds = [
    ...new Set(
      lines
        .map((l) => l.companyId)
        .filter((id): id is string => id != null && id !== "")
    ),
  ];
  if (uniqueIds.length === 0) return "—";
  if (uniqueIds.length === 1) {
    const id = uniqueIds[0];
    const lineWithName = lines.find((l) => l.companyId === id);
    const name = lineWithName?.customerName ?? companyIdToName?.[id];
    return name ?? "—";
  }
  return `${uniqueIds.length} customers`;
}

function getAverageRate(ref: Referral): string {
  const lines = ref.referralLines ?? ref.lines ?? [];
  if (lines.length === 0) return "—";
  const sum = lines.reduce((acc, l) => acc + (l.rate ?? 0), 0);
  const avg = sum / lines.length;
  return `${avg.toFixed(1)}%`;
}

const ReferralsTable: React.FC<ReferralsTableProps> = ({
  referrals,
  quarterLabel,
  onRowClick,
  companyIdToName,
  referralIdToEstimatedCommission,
}) => {
  return (
    <div className="rounded-md border border-gray-200 overflow-hidden bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50 border-b border-gray-201">
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Name
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Email
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Customers
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Rate*
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Est. Commission (
              {quarterLabel}
              )
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {referrals.map((ref) => (
            <TableRow
              key={ref.id}
              className="cursor-pointer hover:bg-gray-50 border-b border-gray-201"
              onClick={() => onRowClick(ref)}
            >
              <TableCell className="font-medium py-4 px-3">
                {ref.name ?? ref.organizationName ?? "—"}
              </TableCell>
              <TableCell className="text-gray-600 py-4 px-3">
                {ref.email ?? ref.contactEmail ?? "—"}
              </TableCell>
              <TableCell className="py-4 px-3 text-gray-600">
                {getCustomersCellContent(ref, companyIdToName)}
              </TableCell>
              <TableCell className="py-4 px-3">
                {getAverageRate(ref)}
              </TableCell>
              <TableCell className="py-4 px-3">
                {referralIdToEstimatedCommission?.[ref.id] != null
                  ? formatCurrency(referralIdToEstimatedCommission[ref.id])
                  : ref.estimatedPayoutForQuarter != null
                    ? formatCurrency(ref.estimatedPayoutForQuarter)
                    : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-gray-500 px-3 py-2 border-t border-gray-200 bg-gray-50/50">
        * Rate is the average of all line rates for this referral.
      </p>
    </div>
  );
};

export default ReferralsTable;
